import ImageIcon from 'components/Icon/ImageIcon';
import TextAreaInput from 'components/TextArea';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { device } from 'styles/breakpoints';
import { ActionButton } from 'styles/global.styles';
import { theme } from 'styles/theme';
import { IconCheckmarkSmall, IconCross, IconQuestionMark } from '../../assets/icons';

export interface IMaterial {
  icon?: string;
  type: string;
  selected: boolean;
  details?: string[];
}

export interface ISavedMaterial {
  type: string;
  description?: string;
}

export interface IMaterialSelector {
  data?: IMaterial[];
  otherWasteValue: string;
  onChangeOtherWaste: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSelectionChange: (index: number, newValue: boolean) => void;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled: boolean;
  error?: boolean | string;
}

const MaterialSelector = ({
  data,
  onChangeOtherWaste,
  otherWasteValue,
  onSelectionChange,
  onClick,
  disabled,
  error,
}: IMaterialSelector): JSX.Element => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<IMaterial | null>(null);

  const togglePanel = (material: IMaterial | null) => {
    setSelectedMaterial(material);
    setIsPanelOpen(!isPanelOpen);
  };

  useEffect(() => {
    if (isPanelOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isPanelOpen]);

  return (
    <>
      <MaterialGrid>
        {data?.map((item: IMaterial, index: number) => (
          <MaterialItem
            $selected={item.selected}
            key={index}
            onClick={(e) => onSelectionChange(index, item.selected)}
          >
            <ImageIcon height={60} src={item.icon} alt={`Material icon ${index}`} />

            <Title $selected={item.selected}>{item.type}</Title>

            <MoreInfo
              onClick={(e) => {
                e.stopPropagation();
                togglePanel(item);
              }}
            >
              <ImageIcon width={28} src={IconQuestionMark} alt="Material more info icon" />
            </MoreInfo>
          </MaterialItem>
        ))}

        <TextAreaWrapper>
          <OtherWasteTextArea
            label="Annat avfall?"
            placeholder="Beskrivning"
            onChange={(event) => onChangeOtherWaste(event)}
            value={otherWasteValue}
          />
        </TextAreaWrapper>
      </MaterialGrid>

      <Overlay $visible={isPanelOpen} onClick={() => togglePanel(null)} />
      <Panel $visible={isPanelOpen}>
        {selectedMaterial && (
          <>
            <IconExit onClick={() => togglePanel(null)}>
              <ImageIcon width={24} src={IconCross} alt="Material details exit icon" />
            </IconExit>
            <MaterialType>{selectedMaterial.type}</MaterialType>
            <Details>
              {selectedMaterial.details?.map((detail: string, i: number) => (
                <DetailItem key={i}>
                  <ImageIcon src={IconCheckmarkSmall} alt="Material details checkmark icon" />
                  <span>{detail}</span>
                </DetailItem>
              ))}
            </Details>
          </>
        )}
      </Panel>

      <ActionButton error={error} disabled={disabled} onClick={onClick}>
        Fortsätt
      </ActionButton>
    </>
  );
};

export default MaterialSelector;

const MaterialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.columns.gap};

  @media ${device.md} {
    grid-template-columns: repeat(3, 1fr);
  }

  @media ${device.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const MaterialItem = styled.div<{ $selected: boolean }>`
  cursor: pointer;

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  gap: ${theme.columns.gap};
  background-color: ${theme.colors.secondary};
  border-radius: ${theme.dimensions.components.borderRadius};

  padding: 0 1rem;
  width: 100%;
  height: 150px;
  word-break: break-all;

  @media ${device.xsm} {
    word-break: unset;
    width: calc(
      ${parseInt(theme.dimensions.card.width) / theme.materials.columns -
      parseInt(theme.columns.gap)}px
    );
    height: calc(
      ${parseInt(theme.dimensions.card.width) / theme.materials.columns -
      parseInt(theme.columns.gap)}px
    );
  }

  border: 2px solid transparent;
  transition:
    border-color 0.3s ease,
    background-color 0.3s ease;

  border-color: ${({ $selected }) =>
    $selected ? `${theme.materials.selectedBorder}` : 'transparent'};

  @media (hover: hover) {
    &:hover {
      border-color: ${({ $selected }) =>
        $selected ? `${theme.materials.selectedBorder}` : `${theme.colors.grayscale}`};
    }

    &:not(:hover):not(:active) {
      border-color: ${({ $selected }) =>
        $selected ? `${theme.materials.selectedBorder}` : 'transparent'};
    }
  }

  @media (active: active) {
    &:active {
      outline: none;
      border-color: ${({ $selected }) =>
        $selected ? `${theme.materials.selectedBorder}` : `${theme.colors.grayscale}`};
    }
  }
`;

const Title = styled.span<{ $selected: boolean }>`
  font-weight: 600;
  font-size: 12px;

  @media ${device.xsm} {
    font-size: 1rem;
  }

  color: ${({ $selected }) =>
    $selected ? `${theme.colors.primary}` : `${theme.colors.grayscale}`};
  transition: color 0.3s ease;
`;

const SvgButtonWrapper = styled.div`
  -webkit-user-select: none;
  user-select: none;
  cursor: pointer;
`;

const MoreInfo = styled(SvgButtonWrapper)`
  position: absolute;
  display: flex;
  top: -8px;
  right: -8px;
`;

const TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;

  background-color: ${theme.colors.secondary};
  border-radius: ${theme.dimensions.components.borderRadius};
  padding: 1rem;
  height: 100%;
  grid-column: 1 / -1;
`;

const OtherWasteTextArea = styled(TextAreaInput)`
  resize: none;
  height: 100px;
`;

const Overlay = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.grayscale_2};
`;

const Panel = styled.div<{ $visible: boolean }>`
  background-color: ${theme.colors.white};
  height: 100vh;
  min-width: 100%;
  top: 0;
  right: 0;
  position: fixed;
  transform: ${({ $visible }) => ($visible ? 'translateX(0)' : 'translateX(100%)')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  z-index: ${({ $visible }) => ($visible ? '999' : '0')};
  transition:
    transform 0.2s ease-out,
    opacity 0.2s ease-out;

  @media ${device.md} {
    min-width: 50%;
  }

  @media ${device.lg} {
    min-width: 25%;
  }
`;

const Details = styled.div<{ $open?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.columns.gap};
  padding: 1rem;
`;

const MaterialType = styled.h1`
  text-align: center;
  font-weight: 700;
  font-size: 20px;
`;

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconExit = styled(SvgButtonWrapper)`
  position: absolute;
  top: 10px;
  right: 10px;
`;
